import { Component, Input, SimpleChanges, EventEmitter, Output, effect, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { balanceIconClass, balanceIcons, coinIcon, useFallbackIcon } from '@olv/ui-shared/utils/asset-balance.utils';
import {
  WalletBalances,
  WalletDelegatedResources,
  WalletLifecycleDetails,
  WalletLifecycleStateEnum,
} from '@olv/ui-shared/models/wallet.model';
import { formatDateStandard } from '@olv/ui-shared/utils/dateFormat.utils';
import { shortenAddress } from '@olv/ui-shared/utils/shortenAddress.utils';
import { InfoCardComponent, InfoCardVariant } from '../info-card/info-card.component';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { SplitPartsPipe } from '@olv/ui-shared/pipes/split-parts.pipe';
import { OtpVerificationDialogComponent } from '../otp-verification-dialog/otp-verification-dialog.component';
import { finalize } from 'rxjs';
import { SystemService } from '@olv/ui-shared/services/system.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { AuthService } from '@olv/ui-shared/services/auth.service';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { RiskDistributionTooltipComponent } from '../risk-distribution-tooltip/risk-distribution-tooltip.component';

export interface BalanceView {
  currency: string;
  amount: number;
  precision: number;
  iconClass: string;
  icons: string[];
}

export interface EntityData {
  walletBalances?: WalletBalances;
}

interface LifecycleCard {
  variant: InfoCardVariant;
  title: string;
  value?: string | number;
  progress?: number;
  progressLabel?: string;
}

@Component({
  selector: 'app-financial-summary-header',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    HasPermissionDirective,
    SplitPartsPipe,
    NgxSkeletonLoaderComponent,
    RiskDistributionTooltipComponent,
    InfoCardComponent,
  ],
  templateUrl: './financial-summary-header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./financial-summary-header.component.scss'],
})
export class FinancialSummaryHeaderComponent implements OnDestroy {
  private router = inject(Router);

  protected readonly useFallbackIcon = useFallbackIcon;
  protected readonly shortenAddress = shortenAddress;
  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  private readonly appCtx = inject(AppContextService);
  private toastrService = inject(ToastService);
  private systemService = inject(SystemService);
  private dialog = inject(MatDialog);
  private auth = inject(AuthService);
  private readonly metadata = inject(NetworkMetadataService);

  @Input() entityName = '';
  @Input() entityAddress = '';
  @Input() entityData: EntityData = {} as EntityData;
  @Input() logoBase64?: string;
  @Input() isActive?: boolean;
  @Input() risk?: string;
  @Input() networkType?: number;
  @Input() walletResourceType?: 'merchant' | 'system';
  @Input() isLoading = false;
  isUpdatingSweeping = false;
  @Input() balanceError: string | null = null;
  @Input() lifecycleDetails?: WalletLifecycleDetails;
  @Input() lifecycleState?: number;
  @Input() delegatedResources?: WalletDelegatedResources | null;
  @Input() isServing?: boolean;
  @Input() layout: 'default' | 'dashboard' = 'default';
  @Input() balanceLabel = '';
  @Input() showAssetsButton = false;
  @Input() assetsOpen = false;
  @Output() toggleAssets = new EventEmitter<void>();
  @Input() canMoveNonSpendableFunds = false;
  private readonly lifecycleStates = ['active', 'capped', 'closed', 'serving'];
  @Output() sweepOpen: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() moveNonSpendableFundsRequested = new EventEmitter<void>();
  balances: BalanceView[] = [];
  isDashboard = false;
  isSettlement = false;
  isSweepingEnabled = false;
  isWalletDetails = false;
  tooltipText = 'Copy';
  hoveredCurrencyIndex: number | null = null;
  tooltipLeft = 0;
  readonly tooltipWidth = 500;
  readonly tooltipViewportPadding = 16;
  private hideTooltipTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const metadataLoaded = this.metadata.currencies().length > 0;
      const raw = this.entityData?.walletBalances?.currencyBalances;
      if (metadataLoaded && raw) this.balances = this.mapBalances(raw);
    });
  }

  ngOnInit() {
    this.metadata.load();
    const appName = this.appCtx.getAppName();
    this.isDashboard = this.router.url.startsWith('/dashboard');
    this.isSettlement = appName === 'settlement';
    this.isWalletDetails = this.router.url.startsWith('/wallet-detail');

    if (this.entityData?.walletBalances?.currencyBalances) {
      this.balances = this.mapBalances(this.entityData.walletBalances.currencyBalances);
    }

    if (
      (this.walletResourceType === 'system' || this.isDashboard) &&
      this.auth.hasPermission(Resources.Dashboard, Scopes.ReadSystemConfiguration)
    ) {
      this.loadSystemConfiguration();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['entityData'] && this.entityData?.walletBalances?.currencyBalances) {
      this.balances = this.mapBalances(this.entityData.walletBalances.currencyBalances);
    }
  }

  get walletResource() {
    return this.walletResourceType === 'merchant' ? Resources.MerchantWallets : Resources.SystemWallets;
  }

  get hasNothingSpendable(): boolean {
    const balances = this.entityData.walletBalances?.currencyBalances ?? [];
    return (
      balances.length === 0 ||
      balances.every((b) => !Number.isFinite(Number(b.availableAmountInCurrency)) || Number(b.availableAmountInCurrency) <= 0)
    );
  }

  get isSweepDisabled(): boolean {
    return this.hasNothingSpendable;
  }

  get sweepTooltipText(): string {
    if (this.hasNothingSpendable) {
      return 'No available balance to sweep';
    }
    return 'Sweep wallet';
  }
  get lifecycleBadge(): string | null {
    const state = this.lifecycleState == null ? null : WalletLifecycleStateEnum[this.lifecycleState]?.toLowerCase();
    return state && this.lifecycleStates.includes(state) ? state : null;
  }

  get showLifecycleBadge(): boolean {
    return !!this.isServing || !!this.lifecycleBadge;
  }

  get lifecycleText(): string {
    if (this.isServing) {
      return 'Serving';
    }
    const state = this.lifecycleBadge;
    return state ? state.charAt(0).toUpperCase() + state.slice(1) : '';
  }

  get lifecycleCards(): LifecycleCard[] {
    const d = this.lifecycleDetails;
    if (!d) return [];

    const suffix = d.baseCurrency ? ` (${d.baseCurrency})` : '';
    const cards: LifecycleCard[] = [];

    if (d.storedMaxCap != null) {
      cards.push({
        variant: 'plain',
        title: `Stored Max Cap${suffix}`,
        value: this.formatAmount(d.storedMaxCap, d.baseCurrencyDisplayPrecision),
      });
    }
    if (d.provisionedAt) {
      cards.push({ variant: 'plain', title: 'Provisioned', value: formatDateStandard(d.provisionedAt) });
    }
    if (d.timeSinceProvisioned) {
      cards.push({ variant: 'plain', title: 'Time since provisioned', value: d.timeSinceProvisioned });
    }
    if (d.rotatedAt) {
      cards.push({ variant: 'plain', title: 'Rotated At', value: formatDateStandard(d.rotatedAt) });
    }
    if (d.timeSinceRotated) {
      cards.push({ variant: 'plain', title: 'Time since rotated', value: d.timeSinceRotated });
    }
    if (d.rotationProgress != null) {
      cards.push({
        variant: 'progress',
        title: 'Rotation Progress',
        progress: d.rotationProgress,
        progressLabel: d.rotationTimeLeft ?? '',
      });
    }

    return cards;
  }

  get delegatedCards(): LifecycleCard[] {
    const d = this.delegatedResources;
    if (!d) return [];

    const cards = [
      ...this.delegationPair('Energy', d.delegatedEnergy, d.usedEnergy),
      ...this.delegationPair('Bandwidth', d.delegatedBandwidth, d.usedBandwidth),
    ];
    if (!cards.length) return [];

    // Withdrawals served, not capacity: real withdrawals often cost less than the baseline, so used can pass allocated.
    return [
      {
        variant: 'plain',
        title: 'Transactions allocated',
        value: `${this.formatAmount(d.transactionsUsed ?? 0, 0)}/${this.formatAmount(d.transactionsAllocated ?? 0, 0)}`,
      },
      ...cards,
    ];
  }

  get hasHeaderCards(): boolean {
    return !this.isLoading && this.isWalletDetails && (this.lifecycleCards.length > 0 || this.delegatedCards.length > 0);
  }

  private delegationPair(resource: 'Energy' | 'Bandwidth', delegated: number, used: number): LifecycleCard[] {
    if (!(delegated > 0)) return [];

    const claimed = Math.min(Math.max(used, 0), delegated);

    return [
      { variant: 'plain', title: `Delegated ${resource.toLowerCase()}`, value: this.formatAmount(delegated, 0) },
      {
        variant: 'progress',
        title: `${resource} utilisation`,
        progress: Math.round((claimed / delegated) * 100),
        progressLabel: `${this.formatAmount(claimed, 0)} used`,
      },
    ];
  }

  private formatAmount(value: number, precision = 2): string {
    return value.toLocaleString('en-US', { minimumFractionDigits: precision, maximumFractionDigits: precision });
  }

  private loadSystemConfiguration(): void {
    if (this.walletResourceType !== 'system' && !this.isDashboard) {
      return;
    }

    this.systemService.getSystemConfiguration().subscribe({
      next: (config) => {
        this.isSweepingEnabled = config.isSweepingEnabled;
      },
    });
  }
  private mapBalances(
    raw: Array<{
      currencyId?: string;
      currencySymbol: string;
      currencyDisplayPrecision: number;
      totalAmountInCurrency: number;
      availableAmountInCurrency: number;
    }>,
  ): BalanceView[] {
    return raw.map((cb) => {
      const icons = balanceIcons(cb.currencyId, cb.currencySymbol, this.metadata);
      const iconClass = balanceIconClass(cb.currencyId, cb.currencySymbol, this.metadata);

      return {
        currency: cb.currencySymbol,
        amount: cb.availableAmountInCurrency,
        precision: cb.currencyDisplayPrecision,
        iconClass,
        icons,
      };
    });
  }

  copyToClipboard(value: string): void {
    if (!value) return;

    navigator.clipboard.writeText(value).then(
      () => {
        this.tooltipText = 'Copied';
        setTimeout(() => (this.tooltipText = 'Copy'), 1000);
      },
      () => {
        this.tooltipText = 'Failed';
        setTimeout(() => (this.tooltipText = 'Copy'), 1000);
      },
    );
  }

  toggleSweep() {
    this.sweepOpen.emit(true);
  }

  moveNonSpendableFunds() {
    this.moveNonSpendableFundsRequested.emit();
  }

  showRiskTooltip(index: number, event?: MouseEvent): void {
    this.clearHideTooltipTimeout();

    if (!this.hasRiskDistribution(index)) {
      this.hoveredCurrencyIndex = null;
      return;
    }

    this.hoveredCurrencyIndex = index;

    if (!event) return;

    const card = event.currentTarget as HTMLElement;
    const { left, width } = card.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const padding = this.tooltipViewportPadding;
    const tooltipWidth = Math.min(this.tooltipWidth, Math.max(viewportWidth - padding * 2, 0));
    const maxLeft = Math.max(padding, viewportWidth - tooltipWidth - padding);

    this.tooltipLeft = Math.min(Math.max(left + width / 2 - tooltipWidth / 2, padding), maxLeft) - left;
  }

  private hasRiskDistribution(index: number): boolean {
    return !!this.entityData.walletBalances?.currencyBalances?.[index]?.riskDistribution?.length;
  }

  keepRiskTooltipOpen(index: number): void {
    this.clearHideTooltipTimeout();

    this.hoveredCurrencyIndex = index;
  }

  private clearHideTooltipTimeout(): void {
    if (!this.hideTooltipTimeout) return;

    clearTimeout(this.hideTooltipTimeout);
    this.hideTooltipTimeout = null;
  }

  hideRiskTooltip(): void {
    this.clearHideTooltipTimeout();
    this.hideTooltipTimeout = setTimeout(() => {
      this.hoveredCurrencyIndex = null;
      this.hideTooltipTimeout = null;
    }, 120);
  }

  ngOnDestroy(): void {
    this.clearHideTooltipTimeout();
  }
  riskToolTip(risk: string | undefined): string {
    if (risk === 'unknown') {
      return 'Unknown';
    } else if (risk === 'low') {
      return 'Low';
    } else if (risk === 'medium') {
      return 'Medium';
    } else if (risk === 'high') {
      return 'High';
    } else if (risk === 'severe') {
      return 'Severe';
    } else {
      return 'Not Assessed';
    }
  }
  sweepingStatusUpdate() {
    const dialogRef = this.dialog.open(OtpVerificationDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      restoreFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Verify Sweeping Change',
      },
    });

    const sub = dialogRef.componentInstance.submitOtp.subscribe((otp: string) => {
      this.onOtpSubmitted(otp, dialogRef);
    });

    dialogRef.afterClosed().subscribe(() => {
      sub.unsubscribe();
    });
  }
  private onOtpSubmitted(otp: string, dialogRef: MatDialogRef<OtpVerificationDialogComponent>): void {
    this.isUpdatingSweeping = true;

    const dialog = dialogRef.componentInstance;
    dialog.setLoadingState(true);
    dialogRef.disableClose = true;

    this.systemService
      .updateSystemSweepingStatus(this.isSweepingEnabled, otp)
      .pipe(
        finalize(() => {
          this.isUpdatingSweeping = false;
          this.loadSystemConfiguration();
          dialog.setLoadingState(false);
          dialogRef.disableClose = false;
        }),
      )
      .subscribe({
        next: () => {
          dialogRef.close();
          this.toastrService.success('Sweeping status updated successfully');
        },
        error: (err) => {
          if (err.isOtpError) {
            dialog.setErrorState(err.error?.message);
            dialog.otpForm.reset();
            dialog.focusInput(0);
            return;
          }

          dialogRef.close();
          this.toastrService.error('Failed to update sweeping status');
        },
      });
  }
  get logoText(): string {
    if (!this.entityName) return '';
    const parts = this.entityName.trim().split(/\s+/);
    return parts
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('');
  }
  get showLogoSkeleton(): boolean {
    return (this.isWalletDetails || !this.isDashboard || !this.isSettlement) && this.isLoading;
  }
  get networkIcon(): string | null {
    const network = this.networkType == null ? undefined : this.metadata.network(this.networkType);
    return network?.iconKey ? coinIcon(network.iconKey) : null;
  }
}
